/**
 * Platform Google Tag Manager for the web-store — a SEPARATE layer from the
 * per-salon Meta Pixel (`./metaPixel.js`). GTM fans out Stylon's own GA4
 * property so the platform can see its acquisition/booking/order funnel
 * across every storefront, independent of which salon is being viewed.
 *
 * Env-gated + no-op-when-unset (mirrors metaPixel.js's contract): with no
 * `REACT_APP_GTM_ID`, NOT EVEN `window.dataLayer` is force-created — nothing
 * is injected, nothing is pushed. This makes it safe to ship dark and light
 * up later by just setting the env var. Every export is best-effort and
 * never throws.
 */

import { afterPageLoad } from "./afterPageLoad";

let initializedContainerId = null;

/** Inject the standard GTM loader for `containerId`. No-op on empty/dup id. */
export function initGtm(containerId) {
  if (!containerId || typeof window === "undefined" || typeof document === "undefined") return;
  if (initializedContainerId === containerId) return;
  try {
    /* eslint-disable */
    (function (w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      // The container script waits for the page to load (utils/afterPageLoad,
      // 2026-10); the dataLayer is live from here on, and it replays it.
      afterPageLoad(function () {
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l !== "dataLayer" ? "&l=" + l : "";
        j.async = true;
        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
        // Standard snippet inserts before the first existing <script> — but
        // that assumes one is already on the page. Fall back to appending to
        // <head> (or documentElement) so this still works if there isn't.
        if (f && f.parentNode) {
          f.parentNode.insertBefore(j, f);
        } else {
          (d.head || d.documentElement).appendChild(j);
        }
      });
    })(window, document, "script", "dataLayer", containerId);
    /* eslint-enable */
    initializedContainerId = containerId;
  } catch (_) {
    /* never throw from instrumentation */
  }
}

/** Push an event to the dataLayer. No-op until `initGtm` has run. Never throws. */
export function gtmEvent(event, params = {}) {
  if (!initializedContainerId || typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...params });
  } catch (_) {
    /* ignore */
  }
}

/** Pure — GA4 `purchase` event payload for an order conversion. */
export function buildGa4Purchase({ transactionId, value, currency } = {}) {
  return {
    event: "purchase",
    transaction_id: transactionId,
    value: Number(value) || 0,
    currency: currency || "ILS",
  };
}

/** Pure — GA4 `generate_lead` event payload for a booking conversion. */
export function buildGa4Lead({ value, currency } = {}) {
  return {
    event: "generate_lead",
    value: Number(value) || 0,
    currency: currency || "ILS",
  };
}

/** Test-only: reset module state between specs. Not used in app code. */
export function __resetGtmForTests() {
  initializedContainerId = null;
}
